/* eslint-disable no-param-reassign */
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectSuppressApiUpdates } from '../../../redux/slices/auth';
import { useUpdateStreamingViewMutation } from '../../../redux/streamGoApi';
import { StreamingView } from '../../../redux/streamGoApi/enums';
import { screenshareViews, slidesViews } from '../constants';
import { useGetRoomStreamingDetails } from './useGetRoomStreamingDetails';

interface IUseIsStreamingModeValid {
  isMediaMode: boolean;
  roomId: string;
  streamingView?: StreamingView;
}

const isScreenshareView = (streamingView?: StreamingView) =>
  streamingView && screenshareViews.includes(streamingView);

const isSlidesView = (streamingView?: StreamingView) =>
  streamingView && slidesViews.includes(streamingView);

const DEFAULT_STREAMING_VIEW = StreamingView.WebCamsActiveSpeaker;

interface ICheckScreenshareViewValid {
  hasScreenShare: boolean;
  newStreamingView?: StreamingView;
  streamingView?: StreamingView;
}

const checkScreenshareViewValid = ({
  hasScreenShare,
  newStreamingView,
  streamingView,
}: ICheckScreenshareViewValid) => {
  if (!hasScreenShare && isScreenshareView(streamingView)) {
    newStreamingView = DEFAULT_STREAMING_VIEW;
  }
  return { newStreamingView };
};

interface ICheckSlidesViewValid {
  hasSgVm: boolean;
  newStreamingView?: StreamingView;
  streamingView?: StreamingView;
}

const checkSlidesViewValid = ({
  hasSgVm,
  newStreamingView,
  streamingView,
}: ICheckSlidesViewValid) => {
  if (!hasSgVm && isSlidesView(streamingView)) {
    newStreamingView = DEFAULT_STREAMING_VIEW;
  }
  return { newStreamingView };
};

export const useIsStreamingModeValid = ({
  isMediaMode = false,
  roomId,
  streamingView,
}: IUseIsStreamingModeValid) => {
  const suppressApiUpdates = useSelector(selectSuppressApiUpdates);
  const { hasScreenShare, hasSgVm } = useGetRoomStreamingDetails();
  const [updateStreamingView] = useUpdateStreamingViewMutation();

  useEffect(() => {
    const postRollOrPreroll =
      streamingView &&
      [StreamingView.PostRoll, StreamingView.PreRoll].includes(streamingView);

    // don't try and switch for the following
    // is there is no streaming view
    // if the streaming view is on post roll or preroll
    // if it is on media mode
    // if there is no roomId
    // if alt view or backup egress don't bother trying to adjust
    if (
      suppressApiUpdates ||
      !streamingView ||
      postRollOrPreroll ||
      isMediaMode ||
      !roomId
    ) {
      return;
    }

    let newStreamingView: StreamingView | undefined = streamingView;

    ({ newStreamingView } = checkScreenshareViewValid({
      streamingView,
      hasScreenShare,
      newStreamingView,
    }));

    ({ newStreamingView } = checkSlidesViewValid({
      streamingView,
      hasSgVm,
      newStreamingView,
    }));

    if (!newStreamingView) {
      newStreamingView = DEFAULT_STREAMING_VIEW;
    }

    if (newStreamingView !== streamingView) {
      updateStreamingView({
        roomId,
        StreamingViewAlt: StreamingView.Unset,
        StreamingView: newStreamingView,
        SuppressDefaultUpdate: true,
      });
    }
  }, [
    hasScreenShare,
    hasSgVm,
    isMediaMode,
    roomId,
    streamingView,
    suppressApiUpdates,
    updateStreamingView,
  ]);
};
